import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Menu, Button, Card, Row, Col, List, Typography, Badge, Space, Spin, message } from 'antd';
import type { MenuProps } from 'antd';
import { 
  DashboardOutlined, 
  SettingOutlined, 
  HistoryOutlined,
  PoweroffOutlined,
  CameraOutlined,
  SoundOutlined,
  EnvironmentOutlined,
  MobileOutlined,
  CalendarOutlined,
  TeamOutlined,
  FileTextOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  AUTH_LOST_EVENT,
  fetchAuthSession,
  getCurrentUser,
  getNativeUserIdHint,
  isNativeAuthMode,
  requestNativeSignIn,
  signInWithGoogleRedirect,
  signOut,
  signOutWithHostedUiRedirect,
} from './auth/gateway';
import './aws-config';

import UserMenu from './components/UserMenu';
import NotificationBell from './components/NotificationBell';
import DeviceSelector from './components/DeviceSelector';
import DashboardPage from './pages/DashboardPage';
import DevicePage from './pages/DevicePage';
import LocationPage from './pages/LocationPage';
import PhotoPage from './pages/PhotoPage';
import AudioPage from './pages/AudioPage';
import AudioDetailPage from './pages/AudioDetailPage';
import TranscriptJobDetailPage from './pages/TranscriptJobDetailPage';
import CalendarPage from './pages/CalendarPage';
import PersonsPage from './pages/PersonsPage';
import DiaryPage from './pages/DiaryPage';
import DiaryEventDetailPage from './pages/DiaryEventDetailPage';
import NativeDevicePage from './pages/NativeDevicePage';
import InternalApiDocsPage from './pages/InternalApiDocsPage';
import AsrPlaygroundPage from './pages/AsrPlaygroundPage';
import LocationAnalysisPlaygroundPage from './pages/LocationAnalysisPlaygroundPage';
import { DeviceProvider, useDevice } from './contexts/DeviceContext';
import { PageHeaderContext, PageHeaderState } from './contexts/PageHeaderContext';
import { API_CONFIG } from './config/api';
import { useLocale } from './hooks/useLocale';
import { useWebSocket } from './hooks/useWebSocket';
import { secureLogger } from './utils/secureLogger';
import { sanitizeUserInput } from './utils/sanitizer';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { isNativeDeviceControlEnabled } from './native/deviceBridge';

const { Text } = Typography;

const { Header, Sider, Content } = Layout;

interface DataItem {
  type: string;
  data: unknown;
  timestamp: string;
}

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info' | 'debug';
  message: string;
  timestamp: string;
  read: boolean;
}

interface AuthIdentity {
  id: string;
  email: string;
  name: string;
  picture: string;
}

interface WaitlistState {
  code: 'WAITLIST_REQUIRED' | 'WAITLIST_PENDING' | 'WAITLIST_REJECTED';
  message: string;
  registrationState: string;
}

type UserAccessResult =
  | { status: 'active'; userId: string }
  | { status: 'waitlist'; waitlist: WaitlistState };

const decodeJwtPayload = (token: string) => {
  const base64Url = token.split('.')[1];
  if (!base64Url) {
    throw new Error('Invalid JWT format');
  }

  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }

  const decoded = atob(base64);
  const jsonPayload = decodeURIComponent(
    decoded
      .split('')
      .map((char) => `%${('00' + char.charCodeAt(0).toString(16)).slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
};

const resolvedBackendUserIdCache = new Map<string, string | null>();
const resolveUserInFlightCache = new Map<string, Promise<UserAccessResult | null>>();

const AppContent: React.FC = () => {
  const nativeAuthMode = isNativeAuthMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateDeviceMode, selectedDevice, loadDevices } = useDevice();
  const searchParams = new URLSearchParams(location.search);
  const embedParam = (searchParams.get('embed') || '').toLowerCase();
  const isEmbedMode = embedParam === '1' || embedParam === 'true';
  const embedSection = (searchParams.get('section') || '').toLowerCase();

  
  const { locale, setLocale, t, setUser: setLocaleUser, loadUserLocale, isLocaleLoaded, setIsLocaleLoaded } = useLocale();
  const [user, setUser] = useState<{ user: { id: string; email: string; name: string; picture: string } } | null>(null);
  const [authIdentity, setAuthIdentity] = useState<AuthIdentity | null>(null);
  const [waitlistState, setWaitlistState] = useState<WaitlistState | null>(null);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [realtimeData, setRealtimeData] = useState<DataItem[]>([]);
  const [stats, setStats] = useState({
    photos: 0,
    audio: 0,
    gps: 0
  });
  const [lastCloseEvent, setLastCloseEvent] = useState<CloseEvent | null>(null);
  const [gpsData, setGpsData] = useState<unknown[]>([]);
  const [photoData, setPhotoData] = useState<unknown[]>([]);
  const [audioData, setAudioData] = useState<unknown[]>([]);
  const [pageHeader, setPageHeader] = useState<PageHeaderState | null>(null);
  const resolveMenuFromPath = useCallback((path: string) => {
    if (path === '/' || path.startsWith('/dashboard')) return '1';
    if (path.startsWith('/internal/location-analysis-playground')) return '12';
    if (path.startsWith('/internal/asr-playground')) return '11';
    if (path.startsWith('/internal/docs')) return '10';
    if (path.startsWith('/native-device')) return '9';
    if (path.startsWith('/devices') && path.includes('/location')) return '3';
    if (path.startsWith('/devices') && path.includes('/photos')) return '4';
    if (path.startsWith('/devices') && path.includes('/audio')) return '5';
    if (path.startsWith('/devices')) return '2';
    if (path.startsWith('/photos')) return '4';
    if (path.startsWith('/audio')) return '5';
    if (path.startsWith('/persons')) return '8';
    if (path.startsWith('/dairy')) return '6';
    if (path.startsWith('/calendar')) return '7';
    return '1';
  }, []);

  const [selectedMenu, setSelectedMenu] = useState(() => resolveMenuFromPath(location.pathname));
  const [userDevices, setUserDevices] = useState<unknown[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [deviceMessages, setDeviceMessages] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const postAuthSyncedUserIdRef = useRef<string | null>(null);
  const lastNativeAuthSessionRef = useRef<string | null>(null);
  const nativeDeviceControlEnabled = nativeAuthMode && isNativeDeviceControlEnabled();

  useEffect(() => {
    setSelectedMenu(resolveMenuFromPath(location.pathname));
  }, [location.pathname, resolveMenuFromPath]);

  const resolveEmbedSectionPath = useCallback((section: string): string | null => {
    switch (section) {
      case 'dashboard':
        return '/';
      case 'device':
      case 'devices':
        return '/devices';
      case 'location':
        return selectedDevice ? `/devices/${selectedDevice.id}/location` : '/devices';
      case 'photo':
      case 'photos':
        return selectedDevice ? `/devices/${selectedDevice.id}/photos` : '/devices';
      case 'audio':
        return selectedDevice ? `/devices/${selectedDevice.id}/audio` : '/devices';
      case 'calendar':
        return '/calendar';
      case 'diary':
      case 'history':
      case 'dairy':
        return '/dairy';
      case 'native-device':
        return '/native-device';
      default:
        return null;
    }
  }, [selectedDevice]);

  useEffect(() => {
    if (!isEmbedMode || !embedSection) {
      return;
    }

    const targetPath = resolveEmbedSectionPath(embedSection);
    if (!targetPath || targetPath === location.pathname) {
      return;
    }

    navigate(
      {
        pathname: targetPath,
        search: location.search,
      },
      { replace: true }
    );
  }, [embedSection, isEmbedMode, location.pathname, location.search, navigate, resolveEmbedSectionPath]);
  


  // ダッシュボードページの更新関数を保存
  const [dashboardUpdateHandler, setDashboardUpdateHandler] = useState<((message: any) => void) | null>(null);
  
  // 🧠 最新の Dashboard ハンドラを保持（stale 対策）
  const dashboardUpdateHandlerRef = useRef<typeof dashboardUpdateHandler>(null);
  const [diaryUpdateHandler, setDiaryUpdateHandler] = useState<((message: any) => void) | null>(null);
  const diaryUpdateHandlerRef = useRef<typeof diaryUpdateHandler>(null);
  const diaryMessageQueueRef = useRef<any[]>([]);
  const [personsUpdateHandler, setPersonsUpdateHandler] = useState<((message: any) => void) | null>(null);
  const personsUpdateHandlerRef = useRef<typeof personsUpdateHandler>(null);
  const personsMessageQueueRef = useRef<any[]>([]);

  // デバッグ: setDashboardUpdateHandler の呼び出しを監視
  const setDashboardUpdateHandlerWithLog = useCallback(
    (handler: ((message: any) => void) | null) => {
      secureLogger.debug('🎯 Setting dashboard handler', { hasHandler: !!handler, handlerType: typeof handler });
      setDashboardUpdateHandler(handler);
      dashboardUpdateHandlerRef.current = handler;
      secureLogger.debug('🔄 Updated dashboardUpdateHandlerRef immediately', { hasHandler: !!handler, handlerType: typeof handler });
    },
    [setDashboardUpdateHandler]
  );
  const setDiaryUpdateHandlerWithLog = useCallback(
    (handler: ((message: any) => void) | null) => {
      secureLogger.debug('🎯 Setting diary handler', { hasHandler: !!handler, handlerType: typeof handler });
      setDiaryUpdateHandler(handler);
      diaryUpdateHandlerRef.current = handler;
      secureLogger.debug('🔄 Updated diaryUpdateHandlerRef immediately', { hasHandler: !!handler, handlerType: typeof handler });
    },
    [setDiaryUpdateHandler]
  );
  const setPersonsUpdateHandlerWithLog = useCallback(
    (handler: ((message: any) => void) | null) => {
      secureLogger.debug('🎯 Setting persons handler', { hasHandler: !!handler, handlerType: typeof handler });
      setPersonsUpdateHandler(handler);
      personsUpdateHandlerRef.current = handler;
      secureLogger.debug('🔄 Updated personsUpdateHandlerRef immediately', { hasHandler: !!handler, handlerType: typeof handler });
    },
    [setPersonsUpdateHandler]
  );

  // 🕓 Dashboard が未登録の間に来た通知を一時的に貯める
  const messageQueueRef = useRef<any[]>([]);

  const handleWebSocketMessage = useCallback((message: any) => {
    if (!message || typeof message !== 'object') {
      secureLogger.debug('📨 WS→App received empty message');
      return;
    }
    secureLogger.debug('📨 WS→App received', { type: message.type });

    // ✅ 最新ハンドラを常に参照
    const handler = dashboardUpdateHandlerRef.current;
    secureLogger.debug('🔍 Handler check', { hasHandler: !!handler, handlerType: typeof handler });
    
    if (handler) {
      secureLogger.debug('📤 Forwarding to Dashboard handler', { type: message.type });
      handler(message);
    } else {
      messageQueueRef.current.push(message);
      secureLogger.debug('🕓 Queued WS message (Dashboard not ready)', { type: message.type });
    }

    const diaryHandler = diaryUpdateHandlerRef.current;
    if (diaryHandler) {
      diaryHandler(message);
    } else {
      diaryMessageQueueRef.current.push(message);
    }

    const personsHandler = personsUpdateHandlerRef.current;
    if (personsHandler) {
      personsHandler(message);
    } else {
      personsMessageQueueRef.current.push(message);
    }

    // --- 既存の通知ロジックはそのまま ---
    const notification: Notification = {
      id: Date.now().toString(),
      type: 'debug' as const,
      message: JSON.stringify(message, null, 2),
      timestamp: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [notification, ...prev.slice(0, 99)]);

    if (
      [
        'device_status',
        'battery_update',
        'network_update',
        'device_connected',
        'device_disconnected',
        'battery_status_changed',
        'gnss_status_changed',
        'network_status_changed',
        'system_notification',
        'device_heartbeat',
      ].includes(message.type)
    ) {
      setDeviceMessages(prev => [...prev, { ...message, timestamp: Date.now() }]);

      if (message.type === 'device_connected' || message.type === 'device_disconnected') {
        const notif: Notification = {
          id: Date.now().toString(),
          type: message.type === 'device_connected' ? 'success' : 'warning',
          message:
            message.type === 'device_connected'
              ? `デバイスがリアルタイムモードに切り替わりました`
              : `デバイスがバッチモードに切り替わりました`,
          timestamp: message.timestamp || new Date().toISOString(),
          read: false,
        };
        setNotifications(prev => [notif, ...prev.slice(0, 99)]);

        if (message.device_id) {
          const newMode = message.type === 'device_connected' ? 'realtime' : 'batch';
          updateDeviceMode(message.device_id, newMode);
        }
      }
    }
  }, [updateDeviceMode]);

  // 🧩 Dashboard が登録されたら、キューを flush
  useEffect(() => {
    if (dashboardUpdateHandler && messageQueueRef.current.length > 0) {
      secureLogger.debug('🚀 Flushing queued WS messages', { count: messageQueueRef.current.length });
      messageQueueRef.current.forEach(msg => dashboardUpdateHandler(msg));
      messageQueueRef.current = [];
    }
  }, [dashboardUpdateHandler]);

  useEffect(() => {
    if (diaryUpdateHandler && diaryMessageQueueRef.current.length > 0) {
      secureLogger.debug('🚀 Flushing queued Diary WS messages', { count: diaryMessageQueueRef.current.length });
      diaryMessageQueueRef.current.forEach(msg => diaryUpdateHandler(msg));
      diaryMessageQueueRef.current = [];
    }
  }, [diaryUpdateHandler]);

  useEffect(() => {
    if (personsUpdateHandler && personsMessageQueueRef.current.length > 0) {
      secureLogger.debug('🚀 Flushing queued Persons WS messages', { count: personsMessageQueueRef.current.length });
      personsMessageQueueRef.current.forEach(msg => personsUpdateHandler(msg));
      personsMessageQueueRef.current = [];
    }
  }, [personsUpdateHandler]);

  const { socket, connectionStatus, connect: connectWebSocket } = useWebSocket(user, {
    onMessage: handleWebSocketMessage,
    onClose: (event) => setLastCloseEvent(event),
  });
  


  const loadUserDevices = useCallback(async (userId: string) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      secureLogger.debug('Loading devices session obtained');
      secureLogger.debug('Token exists for device loading', { hasToken: !!token });
      secureLogger.debug('Token validation for device loading', { hasValidToken: !!token && token.length > 0 });
      
      if (!token) {
        secureLogger.error('No valid token found for device loading');
        return;
      }
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();

        setUserDevices(data.devices || []);
        
        // デバイスがない場合は自動的にデバイス画面に遷移
        if ((data.devices || []).length === 0) {
          setSelectedMenu('2');
          if (location.pathname === '/') {
            navigate('/devices');
          }
        }
      }
    } catch (error) {
      secureLogger.error('Failed to load user devices', { error: 'Device loading failed' });
    } finally {
      setDevicesLoaded(true);
    }
  }, [location.pathname, navigate]);



  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'connecting': return 'processing';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return t('connected');
      case 'connecting': return t('connecting');
      case 'error': return t('error');
      default: return t('disconnected');
    }
  };

  const getDataIcon = (type: string) => {
    switch (type) {
      case 'photo': return <CameraOutlined style={{ color: '#1890ff' }} />;
      case 'audio': return <SoundOutlined style={{ color: '#52c41a' }} />;
      case 'gps': return <EnvironmentOutlined style={{ color: '#faad14' }} />;
      default: return null;
    }
  };

  const formatDataContent = (item: DataItem) => {
    const data = item.data as Record<string, unknown>;
    switch (item.type) {
      case 'photo':
        return `位置: ${(data.latitude as number)?.toFixed(4)}, ${(data.longitude as number)?.toFixed(4)}`;
      case 'audio':
        return `音声: "${sanitizeUserInput(String(data.text || ''))}" (信頼度: ${((data.confidence as number) * 100).toFixed(1)}%)`;
      case 'gps':
        return `GPS: ${sanitizeUserInput(String((data.latitude as number)?.toFixed(4)))}, ${sanitizeUserInput(String((data.longitude as number)?.toFixed(4)))} (方向: ${sanitizeUserInput(String((data.direction as number)?.toFixed(0)))}°)`;
      default:
        return sanitizeUserInput(JSON.stringify(item.data));
    }
  };

  const checkUserRegistration = useCallback(async (userId: string, token: string) => {
    const cacheKey = userId;
    const cached = resolvedBackendUserIdCache.get(cacheKey);
    if (cached) {
      return { status: 'active', userId: cached } as UserAccessResult;
    }

    const inFlight = resolveUserInFlightCache.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request = (async (): Promise<UserAccessResult | null> => {
      try {
        secureLogger.info('Checking user registration');

        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}:resolve`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const userData = await response.json();
          secureLogger.info('User is registered');
          resolvedBackendUserIdCache.set(cacheKey, userData.id);
          return { status: 'active', userId: userData.id };
        }

        if (response.status === 403) {
          const errorData = await response.json();
          const detail =
            typeof errorData.details === 'object' && errorData.details !== null && Object.keys(errorData.details).length > 0
              ? errorData.details
              : typeof errorData.detail === 'object' && errorData.detail !== null
              ? errorData.detail
              : {
                  code: 'WAITLIST_REQUIRED',
                  message:
                    typeof errorData.message === 'string'
                      ? errorData.message
                      : typeof errorData.detail === 'string'
                      ? errorData.detail
                      : '利用申請が必要です。',
                  registration_state: 'deactivated',
                };
          return {
            status: 'waitlist',
            waitlist: {
              code: detail.code || 'WAITLIST_REQUIRED',
              message: detail.message || '利用申請が必要です。',
              registrationState: detail.registration_state || 'deactivated',
            },
          };
        } else {
          secureLogger.warn('Unexpected response', { status: response.status });
          return null;
        }
      } catch (error) {
        secureLogger.error('Error checking registration', { error: 'Registration check failed' });
        return null;
      }
    })();

    resolveUserInFlightCache.set(cacheKey, request);
    return request.finally(() => {
      resolveUserInFlightCache.delete(cacheKey);
    });
  }, []);

  const setUserIfChanged = useCallback((nextUser: { id: string; email: string; name: string; picture: string }) => {
    setUser((prev) => {
      if (
        prev?.user?.id === nextUser.id &&
        prev.user.email === nextUser.email &&
        prev.user.name === nextUser.name &&
        prev.user.picture === nextUser.picture
      ) {
        return prev;
      }
      return { user: nextUser };
    });
  }, []);

  const handleDeleteUser = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      if (!token) {
        secureLogger.error('No valid token found for user deletion');
        return;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/me`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        secureLogger.error('User deletion failed', { status: response.status });
        return;
      }

      resolvedBackendUserIdCache.clear();
      resolveUserInFlightCache.clear();
      setUser(null);
      setAuthIdentity(null);
      setWaitlistState(null);
      setAuthInitialized(false);
      await signOut();
      window.location.reload();
    } catch (error) {
      secureLogger.error('User deletion error', { error: 'User deletion failed' });
    }
  }, [setUser]);

  const submitWaitlistRequest = useCallback(async () => {
    try {
      setWaitlistSubmitting(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      if (!token) {
        secureLogger.error('No valid token found for waitlist submission');
        return;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/waitlist`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: authIdentity?.email || '',
          name: authIdentity?.name || '',
          picture_url: authIdentity?.picture || null,
        }),
      });

      if (!response.ok) {
        secureLogger.error('Waitlist submission failed', { status: response.status });
        return;
      }

      const data = await response.json();
      setWaitlistState({
        code: 'WAITLIST_PENDING',
        message: data.message || 'ウェイトリスト申請を受け付けました。',
        registrationState: data.registration_state || 'waitlist_pending',
      });
    } catch (error) {
      secureLogger.error('Waitlist submission error', { error: 'Waitlist submission failed' });
    } finally {
      setWaitlistSubmitting(false);
    }
  }, [authIdentity]);

  const initializeAuth = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      secureLogger.debug('Auth initialization - current user obtained');
      secureLogger.debug('Auth initialization - session obtained');
      secureLogger.debug('Auth initialization - tokens obtained');
      
      // JWTトークンからユーザー情報を取得
      const idToken = session.tokens?.idToken;
      let userInfo = {
        id: currentUser.userId,
        email: currentUser.signInDetails?.loginId || '',
        name: currentUser.username,
        picture: ''
      };
      
      secureLogger.debug('Initial user info created');
      secureLogger.debug('ID Token exists', { hasToken: !!idToken });
      
      if (idToken) {
        try {
          const payload = decodeJwtPayload(idToken.toString());
          secureLogger.debug('JWT payload decoded successfully');
          
          // Google IDをCognitoのusernameから取得し、Google_プレフィックスを除去
          const cognitoUsername = payload['cognito:username'] || payload.sub;
          const googleId = cognitoUsername.startsWith('Google_') ? cognitoUsername.substring(7) : cognitoUsername;
          secureLogger.debug('User info extracted from JWT');
          
          userInfo = {
            id: googleId,
            email: payload.email || userInfo.email,
            name: payload.name || userInfo.name,
            picture: payload.picture || ''
          };
          secureLogger.debug('Final user info created');
        } catch (e) {
          secureLogger.warn('Failed to parse JWT', { error: (e as Error).message });
        }
      }
      setAuthIdentity(userInfo);

      let resolvedUserInfo = userInfo;
      if (session.tokens?.accessToken) {
        const result = await checkUserRegistration(userInfo.id, session.tokens.accessToken.toString());

        if (result?.status === 'active') {
          resolvedUserInfo = {
            ...userInfo,
            id: result.userId,
          };
          setWaitlistState(null);
        } else if (result?.status === 'waitlist') {
          setUser(null);
          setLocaleUser({ user: { id: userInfo.id } });
          setWaitlistState(result.waitlist);
          return;
        }
      }

      secureLogger.debug('Setting user state');
      setUserIfChanged(resolvedUserInfo);
      setWaitlistState(null);
      setLocaleUser({ user: { id: resolvedUserInfo.id } });
    } catch (error) {
      secureLogger.info('No authenticated user found during initialization', { context: 'auth_init' });
      setAuthIdentity(null);
      setWaitlistState(null);
    } finally {
      setIsLocaleLoaded(true);
      setAuthInitialized(true);
    }
  }, [checkUserRegistration, setIsLocaleLoaded, setLocaleUser, setUserIfChanged]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    if (nativeAuthMode) {
      return;
    }

    const handleAuthLost = async (event: Event) => {
      if (!user && !authIdentity) {
        secureLogger.info('Ignoring auth lost event before an authenticated session is established', {
          context: 'auth_lost_pre_auth',
        });
        return;
      }

      const customEvent = event as CustomEvent<{ reason?: string; occurredAt?: string }>;
      secureLogger.warn('Web auth session lost; returning to sign-in screen', {
        reason: customEvent.detail?.reason || 'unknown',
      });

      message.destroy();
      message.warning('セッションの有効期限が切れました。再ログインしてください。');

      try {
        await signOut();
      } catch (error) {
        secureLogger.warn('Best-effort sign-out after auth loss failed', {
          error: error instanceof Error ? error.message : 'unknown',
        });
      }

      setUser(null);
      setAuthIdentity(null);
      setWaitlistState(null);
      setLocaleUser(null);
      setSelectedMenu(resolveMenuFromPath('/'));
      navigate('/', { replace: true });
    };

    window.addEventListener(AUTH_LOST_EVENT, handleAuthLost as EventListener);
    return () => {
      window.removeEventListener(AUTH_LOST_EVENT, handleAuthLost as EventListener);
    };
  }, [authIdentity, nativeAuthMode, navigate, resolveMenuFromPath, setLocaleUser, user]);

  useEffect(() => {
    if (authInitialized) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      secureLogger.warn('Auth initialization timeout fallback');
      setAuthInitialized(true);
    }, 8000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authInitialized]);

  useEffect(() => {
    if (!nativeAuthMode) {
      return;
    }

    const onNativeAuthChanged = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      const detail = customEvent.detail || null;
      if (detail != null && detail === lastNativeAuthSessionRef.current) {
        return;
      }
      lastNativeAuthSessionRef.current = detail;
      initializeAuth();
    };
    const onNativeAuthCleared = () => {
      lastNativeAuthSessionRef.current = null;
      setUser(null);
      setAuthIdentity(null);
      setWaitlistState(null);
      setAuthInitialized(true);
    };

    window.addEventListener('thinklet-native-auth-changed', onNativeAuthChanged as EventListener);
    window.addEventListener('thinklet-native-auth-cleared', onNativeAuthCleared as EventListener);

    return () => {
      window.removeEventListener('thinklet-native-auth-changed', onNativeAuthChanged as EventListener);
      window.removeEventListener('thinklet-native-auth-cleared', onNativeAuthCleared as EventListener);
    };
  }, [initializeAuth, nativeAuthMode]);

  useEffect(() => {
    if (!nativeAuthMode || user?.user?.id) {
      return;
    }

    const retry1 = window.setTimeout(() => {
      initializeAuth();
    }, 500);
    const retry2 = window.setTimeout(() => {
      initializeAuth();
    }, 1500);

    return () => {
      window.clearTimeout(retry1);
      window.clearTimeout(retry2);
    };
  }, [initializeAuth, nativeAuthMode, user?.user?.id]);

  useEffect(() => {
    const userId = user?.user?.id;
    if (!userId) {
      postAuthSyncedUserIdRef.current = null;
      return;
    }

    if (postAuthSyncedUserIdRef.current === userId) {
      return;
    }
    postAuthSyncedUserIdRef.current = userId;

    setTimeout(() => connectWebSocket(), 100);
    loadUserLocale({ user: { id: userId } });
    loadUserDevices(userId);
    loadDevices();
  }, [connectWebSocket, loadDevices, loadUserDevices, loadUserLocale, user?.user?.id]);



  const menuItems: MenuProps['items'] = [
    {
      key: '1',
      icon: <DashboardOutlined />,
      label: t('dashboard'),
    },
    {
      key: '2',
      icon: <MobileOutlined />,
      label: 'デバイス',
    },
    {
      key: '3',
      icon: <EnvironmentOutlined />,
      label: t('location'),
    },
    {
      key: '4',
      icon: <CameraOutlined />,
      label: '写真',
    },
    {
      key: '5',
      icon: <SoundOutlined />,
      label: '音声',
    },
    {
      key: '8',
      icon: <TeamOutlined />,
      label: t('persons'),
    },
    {
      key: '6',
      icon: <HistoryOutlined />,
      label: t('history'),
    },
    {
      key: '7',
      icon: <CalendarOutlined />,
      label: 'カレンダー',
    },
    {
      type: 'divider',
    },
    {
      type: 'group',
      label: '開発用',
      children: [
        {
          key: '10',
          icon: <FileTextOutlined />,
          label: 'API Docs',
        },
        {
          key: '11',
          icon: <ExperimentOutlined />,
          label: 'ASR Playground',
        },
        {
          key: '12',
          icon: <ExperimentOutlined />,
          label: 'Location Analysis Playground',
        },
      ],
    },
  ];

  if (nativeDeviceControlEnabled) {
    const devGroup = menuItems.find((item): item is NonNullable<MenuProps['items']>[number] & { type: 'group'; children: NonNullable<MenuProps['items']> } =>
      Boolean(item && item.type === 'group' && item.label === '開発用'),
    );
    if (devGroup && devGroup.type === 'group' && devGroup.children) {
      devGroup.children.push({
        key: '9',
        icon: <SettingOutlined />,
        label: 'アプリ連携',
      });
    }
  }





  // 認証初期化中は明示的にローディングを表示する
  if (!authInitialized) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text type="secondary">認証を初期化しています...</Text>
        </Space>
      </div>
    );
  }

  if (!user?.user?.id && nativeAuthMode) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', padding: '20px 0' }}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <img
            src="/logo-blue.svg"
            alt="THINKLET"
            style={{ height: '60px', width: 'auto' }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
          <div
            style={{
              width: 400,
              padding: '24px',
              background: '#fff',
              border: '1px solid #e8e8e8',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
            }}
          >
            <h3 style={{ margin: '0 0 24px 0', fontSize: '16px', fontWeight: 600 }}>
              THINKLET Journal
            </h3>
            <div style={{ marginBottom: 24 }}>
              <Text>サインインしてアプリを開始します。</Text>
            </div>
            <Button
              type="primary"
              size="large"
              onClick={() => requestNativeSignIn()}
              style={{ width: '100%', backgroundColor: '#047D95', borderColor: '#047D95' }}
            >
              Googleでサインイン
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user?.user?.id) {
    if (authIdentity && waitlistState) {
      const showApplyButton = waitlistState.code !== 'WAITLIST_PENDING';

      return (
        <div style={{ minHeight: '100vh', background: '#fff', padding: '20px 0' }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <img
              src="/logo-blue.svg"
              alt="THINKLET"
              style={{ height: '60px', width: 'auto' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
            <div
              style={{
                width: 460,
                padding: '24px',
                background: '#fff',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                textAlign: 'left',
              }}
            >
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
                THINKLET Journal
              </h3>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text strong>{authIdentity.name || authIdentity.email}</Text>
                <Text type="secondary">{authIdentity.email}</Text>
                <Text>{waitlistState.message}</Text>
                <Text type="secondary">
                  ステータス: {waitlistState.registrationState}
                </Text>
                {showApplyButton ? (
                  <Button
                    type="primary"
                    size="large"
                    loading={waitlistSubmitting}
                    onClick={() => submitWaitlistRequest()}
                    style={{ width: '100%', backgroundColor: '#047D95', borderColor: '#047D95' }}
                  >
                    ウェイトリストに申請する
                  </Button>
                ) : (
                  <Text type="secondary">
                    申請済みです。管理者承認後に利用可能になります。
                  </Text>
                )}
                <Button
                  size="large"
                  onClick={async () => {
                    await signOutWithHostedUiRedirect();
                  }}
                  style={{ width: '100%' }}
                >
                  ログアウト
                </Button>
              </Space>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ minHeight: '100vh', background: '#fff', padding: '20px 0' }}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <img
            src="/logo-blue.svg"
            alt="THINKLET"
            style={{ height: '60px', width: 'auto' }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
          <div
            style={{
              width: 400,
              padding: '24px',
              background: '#fff',
              border: '1px solid #e8e8e8',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
            }}
          >
            <h3 style={{ margin: '0 0 24px 0', fontSize: '16px', fontWeight: 600 }}>
              THINKLET Journal
            </h3>
            <div style={{ marginBottom: 24 }}>
              <Text>Google アカウントでサインインしてアプリを開始します。</Text>
            </div>
            <Button
              type="primary"
              size="large"
              onClick={() => signInWithGoogleRedirect()}
              style={{ width: '100%', backgroundColor: '#047D95', borderColor: '#047D95' }}
            >
              Googleでサインイン
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const appRoutes = (
    <Routes>
      <Route path="/" element={
        <DashboardPage 
          notifications={notifications}
          onMarkAsRead={(id) => {
            setNotifications(prev =>
              prev.map(n => n.id === id ? { ...n, read: true } : n)
            );
          }}
          onWebSocketMessage={setDashboardUpdateHandlerWithLog}
          isRealtime={true}
        />
      } />
      <Route
        path="/devices/*"
        element={<DevicePage isSocketConnected={connectionStatus === 'connected'} />}
      />
      <Route path="/location" element={<LocationPage />} />
      <Route path="/devices/:deviceId/location" element={<LocationPage />} />
      <Route
        path="/photos"
        element={
          selectedDevice ? (
            <Navigate to={`/devices/${selectedDevice.id}/photos`} replace />
          ) : (
            <Navigate to="/devices" replace />
          )
        }
      />
      <Route
        path="/audio"
        element={
          selectedDevice ? (
            <Navigate to={`/devices/${selectedDevice.id}/audio`} replace />
          ) : (
            <Navigate to="/devices" replace />
          )
        }
      />
      <Route path="/devices/:deviceId/photos" element={<PhotoPage />} />
      <Route path="/devices/:deviceId/audio" element={<AudioPage />} />
      <Route path="/devices/:deviceId/audio-series/:seriesId" element={<TranscriptJobDetailPage />} />
      <Route path="/audio/:audioId" element={<AudioDetailPage />} />
      <Route path="/persons" element={<PersonsPage onWebSocketMessage={setPersonsUpdateHandlerWithLog} />} />
      <Route path="/history" element={<Navigate to="/dairy" replace />} />
      <Route path="/dairy" element={<DiaryPage onWebSocketMessage={setDiaryUpdateHandlerWithLog} />} />
      <Route path="/dairy/events/:eventIndex" element={<DiaryEventDetailPage />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/internal/docs" element={<InternalApiDocsPage />} />
      <Route path="/internal/asr-playground" element={<AsrPlaygroundPage />} />
      <Route path="/internal/location-analysis-playground" element={<LocationAnalysisPlaygroundPage />} />
      <Route
        path="/native-device"
        element={nativeDeviceControlEnabled ? <NativeDevicePage /> : <Navigate to="/" replace />}
      />
    </Routes>
  );

  const contentStyle = isEmbedMode
    ? { margin: 0, padding: 0, background: '#fff' }
    : { margin: '16px 16px', padding: 24, background: '#fff' };

  const fallbackPageTitle = (() => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/dashboard')) return 'ダッシュボード';
    if (path.startsWith('/internal/location-analysis-playground')) return 'Location Analysis Playground';
    if (path.startsWith('/devices') && path.includes('/location')) return '位置情報';
    if (path.startsWith('/dairy')) return '日記';
    if (path.startsWith('/location')) return '位置情報';
    return '';
  })();

  const headerTitle = pageHeader?.title ?? fallbackPageTitle;

  if (isEmbedMode) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Layout>
          <Content style={contentStyle}>{appRoutes}</Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <PageHeaderContext.Provider value={{ pageHeader, setPageHeader }}>
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
        <div style={{ 
          height: 64, 
          margin: '16px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'flex-start',
          position: 'relative'
        }}>
          <img 
            src="/logo.svg"
            alt="THINKLET" 
            style={{ 
              height: '40px',
              width: 'auto',
              transition: collapsed ? 'opacity 0.15s ease-in-out' : 'none',
              transitionDelay: collapsed ? '0s' : '0s',
              opacity: collapsed ? 0 : 1,
              position: collapsed ? 'absolute' : 'static',
              objectFit: 'contain',
              marginLeft: '-3px'
            }}
            onError={(e) => {
              secureLogger.error('Logo failed to load');
            }}
          />
          <img 
            src="/logo-mini.svg"
            alt="THINKLET" 
            style={{ 
              height: '48px',
              width: '48px',
              transition: collapsed ? 'opacity 0.15s ease-in-out' : 'none',
              transitionDelay: collapsed ? '0.05s' : '0s',
              opacity: collapsed ? 1 : 0,
              position: collapsed ? 'static' : 'absolute',
              objectFit: 'contain'
            }}
            onError={(e) => {
              secureLogger.error('Logo mini failed to load');
            }}
          />
        </div>
        <Menu 
          theme="dark" 
          selectedKeys={[selectedMenu]} 
          items={menuItems} 
          onClick={({ key }) => {
            setSelectedMenu(key);
            switch (key) {
              case '1':
                navigate('/');
                break;
              case '2':
                navigate('/devices');
                break;
              case '3':
                if (selectedDevice) {
                  navigate(`/devices/${selectedDevice.id}/location`);
                } else {
                  navigate('/devices');
                }
                break;
              case '4':
                if (selectedDevice) {
                  navigate(`/devices/${selectedDevice.id}/photos`);
                } else {
                  navigate('/devices');
                }
                break;
              case '5':
                if (selectedDevice) {
                  navigate(`/devices/${selectedDevice.id}/audio`);
                } else {
                  navigate('/devices');
                }
                break;
              case '8':
                navigate('/persons');
                break;
              case '6':
                navigate('/dairy');
                break;
              case '7':
                navigate('/calendar');
                break;
              case '10':
                navigate('/internal/docs');
                break;
              case '11':
                navigate('/internal/asr-playground');
                break;
              case '12':
                navigate('/internal/location-analysis-playground');
                break;
              case '9':
                navigate('/native-device');
                break;
            }
          }}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: '8px 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            minHeight: 64,
            height: 'auto',
            flexWrap: 'wrap',
            lineHeight: 'normal',
          }}
        >
          <div
            style={{
              minWidth: 280,
              flex: '1 1 520px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            {headerTitle && (
              <Text strong style={{ fontSize: 20, lineHeight: '28px', whiteSpace: 'nowrap' }}>
                {headerTitle}
              </Text>
            )}
            {pageHeader?.controls && (
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', flex: '1 1 360px' }}>
                {pageHeader.controls}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0, marginLeft: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Badge status={getStatusColor()} text={getStatusText()} />
              {lastCloseEvent && connectionStatus !== 'connected' && (
                <span style={{ fontSize: '12px', color: '#666', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  [Close: {lastCloseEvent.code} {lastCloseEvent.reason || 'No reason'} {lastCloseEvent.wasClean ? 'Clean' : 'Unclean'}]
                </span>
              )}
              <Button
                type={connectionStatus === 'connected' ? 'primary' : 'default'}
                icon={<PoweroffOutlined />}
                onClick={connectWebSocket}
                disabled={connectionStatus === 'connected'}
              >
                {t('reconnect')}
              </Button>
            </div>
            <DeviceSelector />
            <NotificationBell 
              notifications={notifications}
              onMarkAsRead={(id) => {
                setNotifications(prev => 
                  prev.map(n => n.id === id ? { ...n, read: true } : n)
                );
              }}
              onClearAll={() => setNotifications([])}
            />
            {user?.user && (
              <UserMenu
                user={user.user}
                locale={locale}
                setLocale={setLocale}
                onDeleteUser={handleDeleteUser}
              />
            )}
          </div>
        </Header>
        <Content style={contentStyle}>{appRoutes}</Content>
      </Layout>
    </Layout>
    </PageHeaderContext.Provider>
  );
};

const App: React.FC = () => {
  if (isNativeAuthMode()) {
    return (
      <Router>
        <DeviceProvider userId={getNativeUserIdHint()}>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </DeviceProvider>
      </Router>
    );
  }

  return (
    <Router>
      <DeviceProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </DeviceProvider>
    </Router>
  );
};

export default App;
